import { Add, TrashCan } from '@carbon/icons-react';
import { Button, FormGroup, IconButton, InlineLoading, InlineNotification, Stack, TextArea, TextInput } from '@carbon/react';
import { asProblem, problemMessage } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ErrorNotice } from '../common/Notices';
import { cleanSender, COLOUR, MAX_FOOTER, MAX_LINK_LABEL, MAX_LINKS, MAX_URL, safeImage, safeLink, senderValid } from './emailModel';
import { EmailPreview } from './EmailPreview';
import { EmailSenderFields } from './EmailSenderFields';
import { brandEmailKey, getBrandEmail, SAMPLE_VALUES, saveBrandEmail, type BrandEmail } from './messagingApi';

/**
 * A brand's email settings (ADR 0125): who its service and marketing emails are from (SMTP profile and sender), and the layout every
 * email of the brand is wrapped in (logo, colours, footer, links), with a preview.
 */
export function BrandEmailPanel({ brandId, canEdit }: { brandId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const settings = useQuery({ queryKey: brandEmailKey(brandId), queryFn: () => getBrandEmail(brandId) });
  if (settings.error) return <ErrorNotice title={problemMessage(t, asProblem(settings.error))} onRetry={() => void settings.refetch()} />;
  if (!settings.data) return <InlineLoading description={t('common.loading')} />;
  // A saved version comes back with a new version: the form starts again from it.
  return <BrandEmailForm key={`${brandId}:${settings.data.version}`} brandId={brandId} settings={settings.data} canEdit={canEdit} />;
}

type LinkRow = { key: number; label: string; url: string };

function BrandEmailForm({ brandId, settings, canEdit }: { brandId: string; settings: BrandEmail; canEdit: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [service, setService] = useState(settings.service);
  const [marketing, setMarketing] = useState(settings.marketing);
  const [logoUrl, setLogoUrl] = useState(settings.logoUrl ?? '');
  const [accentColor, setAccentColor] = useState(settings.accentColor);
  const [backgroundColor, setBackgroundColor] = useState(settings.backgroundColor);
  const [footer, setFooter] = useState(settings.footer);
  const [links, setLinks] = useState<LinkRow[]>(() => settings.links.map((l, key) => ({ key, ...l })));
  const [submitted, setSubmitted] = useState(false);
  const [saved, setSaved] = useState(false);

  const badLogo = !!logoUrl.trim() && !safeImage(logoUrl);
  const badAccent = !COLOUR.test(accentColor.trim());
  const badBackground = !COLOUR.test(backgroundColor.trim());
  const badLink = (l: LinkRow) => ({ label: !l.label.trim() || l.label.length > MAX_LINK_LABEL, url: !safeLink(l.url) });
  const invalid =
    badLogo || badAccent || badBackground || footer.length > MAX_FOOTER || !senderValid(service) || !senderValid(marketing) ||
    links.some((l) => badLink(l).label || badLink(l).url);

  const save = useMutation({
    mutationFn: () =>
      saveBrandEmail(brandId, {
        service: cleanSender(service),
        marketing: cleanSender(marketing),
        logoUrl: logoUrl.trim() || null,
        accentColor: accentColor.trim(),
        backgroundColor: backgroundColor.trim(),
        footer: footer.trim(),
        links: links.map((l) => ({ label: l.label.trim(), url: l.url.trim() })),
        version: settings.version,
      }),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: brandEmailKey(brandId) });
    },
  });

  // A sample of the brand's emails, in the templates' rich text (ADR 0125).
  const sample = useMemo(
    () =>
      [
        `## ${t('messaging.email.sample.heading')}`,
        t('messaging.email.sample.text'),
        `[${t('messaging.email.sample.button')}](https://example.com)`,
      ].join('\n\n'),
    [t],
  );
  const change = <T,>(set: (v: T) => void) => (v: T) => {
    setSaved(false);
    set(v);
  };

  return (
    <form
      noValidate
      className="pos-brand-email"
      aria-label={t('messaging.email.settings.title')}
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitted(true);
        if (!invalid) save.mutate();
      }}
    >
      <Stack gap={7} className="pos-brand-email__fields">
        <p className="pos-messages__secondary">{t('messaging.email.settings.intro')}</p>
        <EmailSenderFields
          id="brand-email-service"
          legend={t('messaging.email.settings.service')}
          brandId={brandId}
          value={service}
          onChange={change(setService)}
          submitted={submitted}
          defaultLabel={t('messaging.email.settings.accountDefault')}
          disabled={!canEdit}
        />
        <EmailSenderFields
          id="brand-email-marketing"
          legend={t('messaging.email.settings.marketing')}
          brandId={brandId}
          value={marketing}
          onChange={change(setMarketing)}
          submitted={submitted}
          defaultLabel={t('messaging.email.settings.accountDefault')}
          disabled={!canEdit}
        />
        <FormGroup legendText={t('messaging.email.settings.layout')}>
          <Stack gap={5}>
            <TextInput
              id="brand-email-logo"
              labelText={t('messaging.email.settings.logo')}
              helperText={t('messaging.email.settings.logoHelp')}
              value={logoUrl}
              maxLength={MAX_URL}
              disabled={!canEdit}
              invalid={submitted && badLogo}
              invalidText={t('messaging.email.settings.logoInvalid')}
              onChange={(e) => change(setLogoUrl)(e.target.value)}
            />
            <div className="pos-email-sender">
              <ColourInput
                id="brand-email-accent"
                label={t('messaging.email.settings.accent')}
                value={accentColor}
                invalid={submitted && badAccent}
                disabled={!canEdit}
                onChange={change(setAccentColor)}
              />
              <ColourInput
                id="brand-email-background"
                label={t('messaging.email.settings.background')}
                value={backgroundColor}
                invalid={submitted && badBackground}
                disabled={!canEdit}
                onChange={change(setBackgroundColor)}
              />
            </div>
            <TextArea
              id="brand-email-footer"
              labelText={t('messaging.email.settings.footer')}
              helperText={t('messaging.email.settings.footerHelp')}
              value={footer}
              rows={4}
              maxCount={MAX_FOOTER}
              enableCounter
              disabled={!canEdit}
              onChange={(e) => change(setFooter)(e.target.value)}
            />
          </Stack>
        </FormGroup>
        <FormGroup legendText={t('messaging.email.settings.links')}>
          <p className="pos-messages__secondary">{t('messaging.email.settings.linksHelp', { max: MAX_LINKS })}</p>
          {links.map((l, index) => {
            const bad = submitted ? badLink(l) : { label: false, url: false };
            const set = (patch: Partial<LinkRow>) => change(setLinks)(links.map((x) => (x.key === l.key ? { ...x, ...patch } : x)));
            return (
              <div key={l.key} className="pos-messages__parameter">
                <TextInput
                  id={`brand-email-link-label-${l.key}`}
                  labelText={t('messaging.email.settings.linkLabel')}
                  hideLabel={index > 0}
                  value={l.label}
                  maxLength={MAX_LINK_LABEL}
                  disabled={!canEdit}
                  invalid={bad.label}
                  invalidText={t('admin.validation.required')}
                  onChange={(e) => set({ label: e.target.value })}
                />
                <TextInput
                  id={`brand-email-link-url-${l.key}`}
                  labelText={t('messaging.email.settings.linkUrl')}
                  hideLabel={index > 0}
                  value={l.url}
                  maxLength={MAX_URL}
                  disabled={!canEdit}
                  invalid={bad.url}
                  invalidText={t('messaging.email.settings.linkInvalid')}
                  onChange={(e) => set({ url: e.target.value })}
                />
                <IconButton
                  label={`${t('messaging.email.settings.removeLink')} ${l.label}`.trim()}
                  kind="ghost"
                  disabled={!canEdit}
                  onClick={() => change(setLinks)(links.filter((x) => x.key !== l.key))}
                >
                  <TrashCan />
                </IconButton>
              </div>
            );
          })}
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Add}
            disabled={!canEdit || links.length >= MAX_LINKS}
            onClick={() => change(setLinks)([...links, { key: Math.max(-1, ...links.map((l) => l.key)) + 1, label: '', url: '' }])}
          >
            {t('messaging.email.settings.addLink')}
          </Button>
        </FormGroup>
        {save.error && <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(save.error))} />}
        {submitted && invalid && <InlineNotification kind="error" lowContrast hideCloseButton title={t('messaging.email.settings.invalid')} />}
        {canEdit && (
          <div>
            {save.isPending ? (
              <InlineLoading description={t('messaging.routes.saving')} />
            ) : saved ? (
              <InlineLoading status="finished" description={t('messaging.routes.saved')} />
            ) : (
              <Button type="submit">{t('admin.save')}</Button>
            )}
          </div>
        )}
      </Stack>
      <div className="pos-brand-email__preview">
        <h3 className="pos-email-preview__heading">{t('messaging.email.preview.title')}</h3>
        <EmailPreview
          layout={{ logoUrl, accentColor, backgroundColor, footer, links }}
          subject={t('messaging.email.sample.subject')}
          body={sample}
          values={SAMPLE_VALUES}
        />
      </div>
    </form>
  );
}

/** A `#rrggbb` colour, typed or picked. */
function ColourInput({
  id,
  label,
  value,
  invalid,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  invalid: boolean;
  disabled: boolean;
  onChange(value: string): void;
}) {
  const { t } = useTranslation();
  return (
    <div className="pos-colour-input">
      <TextInput
        id={id}
        labelText={label}
        helperText={t('messaging.email.settings.colourHelp')}
        value={value}
        maxLength={7}
        disabled={disabled}
        invalid={invalid}
        invalidText={t('messaging.email.settings.colourInvalid')}
        onChange={(e) => onChange(e.target.value)}
      />
      {/* The browser's own picker (Carbon has none), as on the POS profile's accent (DESIGN_SYSTEM.md §13.3b): same #rrggbb value. */}
      <input
        type="color"
        className="pos-colour-input__picker"
        aria-label={`${t('messaging.email.settings.pick')}: ${label}`}
        value={COLOUR.test(value.trim()) ? value.trim().toLowerCase() : '#000000'}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
